import { defineStore, getActivePinia } from 'pinia'
import { ref, computed } from 'vue'
import type { UserInfo } from '@/types/api'
import { ipcCall } from '@/composables/useIPC'
import { platformEmit } from '@/platform/bridge'
import {
  IpcCommand,
  TauriEvent,
  type EndpointProfileInfo,
  type TauriEventPayloadMap,
} from '@/types/ipc'
import { setTokenProvider, setTokenUpdater } from '@/api/client'
import { getUserInfo as fetchCurrentUserInfo } from '@/api/modules/auth'
import { getMyPermissions } from '@/api/modules/role'
import { cancelRunningAnalysisJobsForOtherIdentity } from '@/services/analysis/AnalysisJobScheduler'
import { clearSentryUserContext, setSentryUserContext } from '@/utils/sentry'
import { initializeCustomerFriendDetectionAccessDate } from '@/services/customerServiceWechatFriendDetectionSync'

type AuthSessionChangedPayload = TauriEventPayloadMap[TauriEvent.AuthSessionChanged]
let initGeneration = 0

export const useAuthStore = defineStore('auth', () => {
  /** Token */
  const token = ref<string>(localStorage.getItem('x-token') || '')

  /** 用户信息 */
  const userInfo = ref<UserInfo | null>(
    (() => {
      try {
        const stored = localStorage.getItem('user-info')
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    })(),
  )
  if (userInfo.value) {
    setSentryUserContext(userInfo.value)
  } else {
    clearSentryUserContext()
  }

  /** 客户端权限码列表 */
  const permissions = ref<string[]>(
    (() => {
      try {
        const stored = localStorage.getItem('user-permissions')
        return stored ? JSON.parse(stored) : []
      } catch {
        return []
      }
    })(),
  )

  /** 启动时读取的 Endpoint Profile，仅保留在当前 Pinia 内存。 */
  const endpointProfileInfo = ref<EndpointProfileInfo | null>(null)

  /** 从客户端权限接口恢复的企业 ID，作为用户资料缺少企业扩展字段时的兜底。 */
  const resolvedCompanyId = ref(0)

  /** 当前有效私有化安装包的标准化公司编码。 */
  const privateCompanyCode = computed(() => {
    const profile = endpointProfileInfo.value
    if (!profile?.present || !profile.valid) return ''
    return profile.companyCode?.trim().toLowerCase() || ''
  })

  const isPrivateEndpointPackage = computed(() => Boolean(privateCompanyCode.value))

  function formatClientLoginAccount(username: string): string {
    if (!isPrivateEndpointPackage.value) return username
    const suffix = `#${privateCompanyCode.value}`
    return username.toLowerCase().endsWith(suffix) ? username : `${username}${suffix}`
  }

  function normalizeClientLoginAccount(username: string): string {
    if (!isPrivateEndpointPackage.value) return username
    const value = username.trim()
    const parts = value.split('#')
    if (parts.length !== 2 || parts[1].trim().toLowerCase() !== privateCompanyCode.value) {
      return value
    }
    return parts[0].trim()
  }

  /** 是否已登录 */
  const isLoggedIn = computed(() => !!token.value)

  /** 当前企业 ID；用户资料暂未恢复时，从当前 Token 的声明中兜底读取。 */
  const companyId = computed(() => {
    const snapshotCompanyId = Number(userInfo.value?.companyId || 0)
    if (Number.isFinite(snapshotCompanyId) && snapshotCompanyId > 0) return snapshotCompanyId
    if (resolvedCompanyId.value > 0) return resolvedCompanyId.value
    return parseTokenClaimNumber(token.value, ['companyId', 'company_id', 'CompanyID', 'companyID'])
  })

  /** 用户角色名 */
  const roleName = computed(() => userInfo.value?.roleName || '')

  /** 当前用户的数据权限范围 */
  const dataScope = computed<DataScope>(() => {
    return userInfo.value?.dataScope || 'self_only'
  })

  /** 功能超级管理员只认服务端标识或客户端企业稳定角色编码，不由 Boss authorityId 或数据范围推断。 */
  const isSuperAdmin = computed(() =>
    Boolean(
      userInfo.value?.isSuperAdmin ||
      userInfo.value?.roles?.some((role) => role.code === 'super_admin'),
    ),
  )

  /** 是否拥有全部数据权限 */
  const isDataScopeAll = computed(() => dataScope.value === 'all')

  /** 是否仅能查看本人数据 */
  const isSelfOnly = computed(() => dataScope.value === 'self_only')

  /**
   * 判断是否拥有指定权限码
   * 功能权限与数据范围相互独立；无权限时不作隐式放行。
   */
  function hasPermission(code: string): boolean {
    if (isSuperAdmin.value) return true
    return permissions.value.includes(code)
  }

  /** 设置 Token（同时注入到 API Client） */
  function setToken(newToken: string) {
    token.value = newToken
    localStorage.setItem('x-token', newToken)
  }

  function buildSessionChangedPayload(source: string): AuthSessionChangedPayload {
    return {
      loggedIn: Boolean(token.value && userInfo.value),
      token: token.value,
      user: userInfo.value,
      permissions: [...permissions.value],
      source,
    }
  }

  async function broadcastAuthSessionChanged(source: string) {
    const payload = buildSessionChangedPayload(source)
    try {
      await platformEmit(TauriEvent.AuthSessionChanged, payload)
      console.info('[Auth] session changed event emitted', {
        source,
        loggedIn: payload.loggedIn,
        userId: payload.user?.id || 0,
        companyId: payload.user?.companyId || 0,
      })
    } catch (e) {
      console.warn('[Auth] session changed event emit failed', { source, error: e })
    }
  }

  async function initializeFriendDetectionAccessMarker() {
    try {
      await initializeCustomerFriendDetectionAccessDate({
        companyId: Number(userInfo.value?.companyId || 0),
        userId: Number(userInfo.value?.id || 0),
      })
    } catch (error) {
      console.warn('[Auth] friend detection access marker unavailable', error)
    }
  }

  async function syncTokenToRust(
    source = 'manual',
    rawToken = token.value,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!rawToken) return false
    try {
      const companyId = Number(userInfo.value?.companyId || 0)
      const userId = Number(userInfo.value?.id || 0)
      await ipcCall(
        IpcCommand.AuthSyncToken,
        {
          token: rawToken,
          ...(companyId > 0 ? { companyId } : {}),
          ...(userId > 0 ? { userId } : {}),
        },
        { timeout: 5000, signal },
      )
      console.info('[Auth] token synced to Rust session', {
        source,
        tokenLen: rawToken.length,
        companyId,
        userId,
      })
      return true
    } catch (e) {
      if (signal?.aborted) throw e
      console.warn('[Auth] token sync to Rust session failed', { source, error: e })
      return false
    }
  }

  function userIdentityKey(info: UserInfo | null | undefined): string {
    const userId = Number(info?.id || 0)
    const companyId = Number(info?.companyId || 0)
    if (!userId) return ''
    return `${companyId}:${userId}`
  }

  function resetRuntimeStoresAfterAccountSwitch(source: string) {
    try {
      const pinia = getActivePinia() as
        | (ReturnType<typeof getActivePinia> & {
            _s?: Map<string, { $reset?: () => void; clearForAccountSwitch?: () => void }>
          })
        | null
      const stores = pinia?._s
      if (!stores) return
      stores.forEach((s, id) => {
        if (id === 'auth') return
        try {
          if (id === 'sidebar' && typeof s.clearForAccountSwitch === 'function') {
            s.clearForAccountSwitch()
            return
          }
          if (typeof s.$reset === 'function') {
            s.$reset()
          }
        } catch {
          // 单个 store 重置失败不影响整体
        }
      })
      console.info('[Auth] runtime stores reset after account switch', { source })
    } catch {
      // Pinia 尚未初始化时忽略
    }
  }

  /** 设置用户信息 */
  function setUserInfo(info: UserInfo) {
    const previousIdentity = userIdentityKey(userInfo.value)
    const nextIdentity = userIdentityKey(info)
    userInfo.value = info
    localStorage.setItem('user-info', JSON.stringify(info))
    setSentryUserContext(info)
    if (previousIdentity !== nextIdentity && token.value) {
      void syncTokenToRust('user_scope_changed')
    }
    if (previousIdentity && nextIdentity && previousIdentity !== nextIdentity) {
      void cancelRunningAnalysisJobsForOtherIdentity({
        companyId: Number(info.companyId),
        userId: Number(info.id),
      }).catch((error) => {
        console.warn('[Auth] cancel running analysis jobs after account switch failed', error)
      })
      resetRuntimeStoresAfterAccountSwitch('setUserInfo')
    }
  }

  /** 只清理用户快照，不清理 token；用于避免旧账号 userInfo 污染当前 token 会话 */
  function clearUserSnapshot() {
    userInfo.value = null
    resolvedCompanyId.value = 0
    permissions.value = []
    localStorage.removeItem('user-info')
    localStorage.removeItem('user-permissions')
    clearSentryUserContext()
    if (token.value) void syncTokenToRust('user_scope_cleared')
  }

  function parseTokenClaims(rawToken: string): Record<string, unknown> | null {
    const payload = rawToken.split('.')[1]
    if (!payload) return null
    try {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
      return JSON.parse(atob(padded)) as Record<string, unknown>
    } catch {
      return null
    }
  }

  function parseTokenClaimNumber(rawToken: string, keys: string[]): number {
    const claims = parseTokenClaims(rawToken)
    if (!claims) return 0
    for (const key of keys) {
      const value = Number(claims[key] || 0)
      if (Number.isFinite(value) && value > 0) return value
    }
    return 0
  }

  function parseTokenUserId(rawToken: string): number {
    return parseTokenClaimNumber(rawToken, ['ID', 'id', 'user_id'])
  }

  function clearStaleUserSnapshotForToken(rawToken: string, source: string) {
    const tokenUserId = parseTokenUserId(rawToken)
    const snapshotUserId = Number(userInfo.value?.id || 0)
    if (!tokenUserId || !snapshotUserId || tokenUserId === snapshotUserId) return
    console.warn('[Auth] stale user-info cleared after token restore', {
      source,
      tokenUserId,
      snapshotUserId,
    })
    clearUserSnapshot()
    resetRuntimeStoresAfterAccountSwitch(source)
  }

  async function refreshCurrentUserInfo(source: string, signal?: AbortSignal) {
    try {
      const info = await fetchCurrentUserInfo({ timeout: 5000, signal })
      if (signal?.aborted) return
      if (info?.id) {
        setUserInfo(info)
      }
    } catch (e) {
      if (signal?.aborted) throw e
      console.warn(`[Auth] ${source}: getUserInfo failed, keeping token session`, e)
    }
  }

  /** 页面进入前确保用户资料已恢复；网络异常时保留 Token 和 JWT 企业范围兜底。 */
  async function ensureUserInfo(source = 'ensureUserInfo'): Promise<UserInfo | null> {
    if (!token.value) return userInfo.value
    if (Number(userInfo.value?.companyId || 0) > 0) return userInfo.value
    await refreshCurrentUserInfo(source)
    if (companyId.value <= 0) {
      await loadPermissions()
    }
    return userInfo.value
  }

  async function readEndpointProfileInfoForStartup(
    signal?: AbortSignal,
  ): Promise<EndpointProfileInfo | null> {
    try {
      const profile = await ipcCall<EndpointProfileInfo>(
        IpcCommand.EndpointProfileInfo,
        undefined,
        { timeout: 5000, signal },
      )
      endpointProfileInfo.value = profile
      return profile
    } catch (e) {
      if (signal?.aborted) throw e
      endpointProfileInfo.value = null
      console.warn('[Auth] initFromIPC: endpoint profile status unavailable', e)
      return null
    }
  }

  /** 设置权限列表 */
  function setPermissions(perms: string[]) {
    permissions.value = perms
    localStorage.setItem('user-permissions', JSON.stringify(perms))
  }

  /** 设置登录状态（兼容旧调用） */
  function setAuth(newToken: string, user: UserInfo) {
    setToken(newToken)
    setUserInfo(user)
  }

  function applyExternalSessionChanged(payload: AuthSessionChangedPayload) {
    if (payload.loggedIn && payload.token && payload.user?.id) {
      setToken(payload.token)
      setUserInfo(payload.user)
      if (Array.isArray(payload.permissions)) {
        setPermissions(payload.permissions)
      }
      return
    }
    clearAuth()
    resetRuntimeStoresAfterAccountSwitch(`external:${payload.source || 'session_changed'}`)
  }

  /** 登录后加载权限（从权限树建立 ID→code 映射） */
  async function loadPermissions() {
    if (!userInfo.value && !token.value) return
    try {
      const res = await getMyPermissions()
      resolvedCompanyId.value = Number(res.resolvedScope?.companyId || 0)
      const current = userInfo.value
      if (current) {
        setUserInfo({
          ...current,
          companyId: Number(current.companyId || resolvedCompanyId.value || 0),
          dataScope: res.dataScope ?? current.dataScope,
          roleName: res.roleName ?? current.roleName,
          roleId: res.roleId ?? current.roleId,
          roleIds: res.roleIds ?? current.roleIds,
          roles: res.roles ?? current.roles,
          resolvedScope: res.resolvedScope ?? current.resolvedScope,
          isSuperAdmin: res.isSuperAdmin ?? current.isSuperAdmin,
        })
      }
      setPermissions(res.codes || [])
    } catch {
      // 权限加载失败时不使用数据范围代替功能权限。
      setPermissions([])
    }
  }

  /** 同步当前登录用户所属企业角色状态 */
  async function syncCurrentRoleState(
    roleId: number,
    updates?: { dataScope?: DataScope; roleName?: string },
  ) {
    const current = userInfo.value
    const assignedRoleIds = current?.roleIds?.length ? current.roleIds : [current?.roleId ?? 0]
    if (!current || !assignedRoleIds.includes(roleId)) return
    if ((updates?.dataScope || updates?.roleName) && current.roleId === roleId) {
      setUserInfo({
        ...current,
        dataScope: updates.dataScope ?? current.dataScope,
        roleName: updates.roleName ?? current.roleName,
      })
    }
    await loadPermissions()
  }

  /** 登录 — 通过 Rust IPC 代理，Token 由 Rust 端安全存储 */
  async function login(credentials: { username: string; password: string; rememberMe?: boolean }) {
    const result = await ipcCall<{ token: string; user: UserInfo }>(
      IpcCommand.AuthLogin,
      credentials as unknown as Record<string, unknown>,
    )
    setToken(result.token)
    setUserInfo(result.user)
    await syncTokenToRust('login', result.token)
    // 登录成功后加载权限（await 确保权限就绪后再渲染主页面）
    await loadPermissions()
    await broadcastAuthSessionChanged('login')
    await initializeFriendDetectionAccessMarker()
    // 登录成功后后台上报设备版本号；连接助手不可用时不阻塞进入主界面。
    void ipcCall(IpcCommand.ReportVersion, undefined, { silent: true }).catch(() => {
      // 版本号上报失败不影响登录流程
    })
    // 登录成功后初始化积分余额 + 定时刷新
    try {
      const { useCreditStore } = await import('./useCreditStore')
      const creditStore = useCreditStore()
      creditStore.fetchStats()
      creditStore.startAutoRefresh()
    } catch {
      // 积分 store 初始化失败不阻塞登录流程
    }
  }

  /** 退出登录 — 关闭侧边栏 + 通知 Rust 清除 Token + 清空前端状态 */
  async function logout() {
    // 先关闭侧边栏窗口
    try {
      await ipcCall(IpcCommand.SidebarClose)
    } catch {
      // 侧边栏未打开时忽略
    }
    try {
      await ipcCall(IpcCommand.AuthLogout)
    } catch {
      // Rust 端清除失败不阻塞退出流程
    }
    // 停止积分定时刷新
    try {
      const { useCreditStore } = await import('./useCreditStore')
      const creditStore = useCreditStore()
      creditStore.stopAutoRefresh()
    } catch {
      // credit store 未初始化时忽略
    }
    clearAuth()
    try {
      const { useBdRoleStore } = await import('./useBdRoleStore')
      useBdRoleStore().reset()
    } catch {
      // ignore
    }
    // 统一重置除 auth 之外的所有 store，避免上一个账号的内存数据残留到下一个账号
    resetRuntimeStoresAfterAccountSwitch('logout')
    await broadcastAuthSessionChanged('logout')
  }

  /**
   * 登录凭证已被服务端明确拒绝时，仅清理本地会话与账号态。
   * Token 刷新链路已负责清理桌面端安全存储，此处不再发起远端注销，避免失效请求循环。
   */
  async function expireSession(source = 'token_expired') {
    clearAuth()
    try {
      const { useCreditStore } = await import('./useCreditStore')
      useCreditStore().stopAutoRefresh()
    } catch {
      // credit store 未初始化时忽略
    }
    try {
      const { useBdRoleStore } = await import('./useBdRoleStore')
      useBdRoleStore().reset()
    } catch {
      // bdRole store 未初始化时忽略
    }
    resetRuntimeStoresAfterAccountSwitch(source)
    await broadcastAuthSessionChanged(source)
  }

  /** 从 IPC 初始化 Token（启动序列用） */
  async function initFromIPC(signal?: AbortSignal) {
    const generation = ++initGeneration
    const ensureCurrent = () => {
      if (signal?.aborted || generation !== initGeneration) {
        const error = new Error('认证初始化已取消')
        error.name = 'AbortError'
        throw error
      }
    }

    const endpointProfile = await readEndpointProfileInfoForStartup(signal)
    ensureCurrent()
    try {
      const storedToken = await ipcCall<string>(IpcCommand.AuthGetToken, undefined, {
        timeout: 5000,
        signal,
      })
      ensureCurrent()
      if (storedToken) {
        setToken(storedToken)
        clearStaleUserSnapshotForToken(storedToken, 'rust')
        await refreshCurrentUserInfo('initFromIPC token restored from Rust/SQLite', signal)
        ensureCurrent()
        console.info('[Auth] initFromIPC: token restored from Rust/SQLite')
      } else if (token.value) {
        if (endpointProfile?.present) {
          console.warn(
            '[Auth] initFromIPC: localStorage token ignored for Endpoint Profile package',
            {
              valid: endpointProfile.valid,
              errorCode: endpointProfile.errorCode || '',
            },
          )
          clearAuth()
          return
        }
        // Rust 无持久化 Token 但 localStorage 仍有 → 同步到 Rust 内存，确保 api_proxy 可注入
        clearStaleUserSnapshotForToken(token.value, 'localStorage')
        await syncTokenToRust('initFromIPC localStorage', token.value, signal)
        ensureCurrent()
        await refreshCurrentUserInfo('initFromIPC token synced from localStorage', signal)
        ensureCurrent()
        console.info('[Auth] initFromIPC: token synced from localStorage to Rust', {
          tokenLen: token.value.length,
        })
      } else {
        console.warn('[Auth] initFromIPC: no token available (localStorage empty, SQLite empty)')
      }
    } catch (e) {
      if (signal?.aborted || generation !== initGeneration) throw e
      if (endpointProfile?.present) {
        console.warn(
          '[Auth] initFromIPC: IPC failed, localStorage fallback disabled for Endpoint Profile package',
          e,
        )
        clearAuth()
        return
      }
      console.warn('[Auth] initFromIPC: IPC failed, using localStorage fallback', e)
    }
    // 启动时已登录则重新拉取客户端企业角色权限，覆盖 localStorage 里的旧权限快照。
    // 菜单/路由只消费客户端企业角色权限，不回退总后台角色。
    if (isLoggedIn.value) {
      await loadPermissions().catch((e) => {
        console.warn('[Auth] initFromIPC: loadPermissions failed, will retry on navigation', e)
      })
      ensureCurrent()
      await initializeFriendDetectionAccessMarker()
      ensureCurrent()
    }
  }

  /** 清除登录状态 */
  function clearAuth() {
    token.value = ''
    localStorage.removeItem('x-token')
    clearUserSnapshot()
  }

  /** 注册 Token Provider + Updater 到 API Client */
  function setupTokenProvider() {
    setTokenProvider(() => token.value)
    setTokenUpdater((newToken: string) => {
      setToken(newToken)
      void syncTokenToRust('tokenUpdater', newToken)
    })
  }

  return {
    token,
    userInfo,
    companyId,
    permissions,
    endpointProfileInfo,
    resolvedCompanyId,
    isLoggedIn,
    roleName,
    isSuperAdmin,
    privateCompanyCode,
    isPrivateEndpointPackage,
    dataScope,
    isDataScopeAll,
    isSelfOnly,
    hasPermission,
    formatClientLoginAccount,
    normalizeClientLoginAccount,
    setToken,
    syncTokenToRust,
    setUserInfo,
    setPermissions,
    setAuth,
    applyExternalSessionChanged,
    login,
    logout,
    expireSession,
    initFromIPC,
    ensureUserInfo,
    clearAuth,
    setupTokenProvider,
    loadPermissions,
    syncCurrentRoleState,
  }
})
